<template>
    <div class="header-section">
      <div class="content-section">
        <h3 class="section-title">{{ $t('newsLetter') }}</h3>
        <div class="newsletter-header flex justify-between items-center mb-4">
          <button 
            @click="exportToExcel" 
            class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center"
          >
            <i class="icon-smsir-file-excel text-xl text-white"></i>
            <span class="text-white text-base">
              {{ $t('newsletter.export_excel') }}
            </span>
          </button>
        </div>

        <div class="space-y-4 content-section mt-5">
          <LoadingSpinner v-if="loading" />

          <div v-else-if="phones.length === 0" class="no-templates p-4 text-center bg-gray-50 rounded-lg">
            <p class="text-gray-500">{{ $t('newsletter.no_subscribers') }}</p>
          </div>

          <div v-else>
            <!-- Search Box -->
            <div class="mb-4">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="$t('newsletter.search_placeholder')"
                class="input-main w-full"
              />
            </div>

            <!-- Table -->
            <div class="overflow-x-auto">
              <table class="w-full border-collapse">
                <thead>
                  <tr class="bg-gray-100">
                    <th class="p-3 text-right w-1/6 text-[#4D4D4D] text-sm font-light">#</th>
                    <th class="p-3 text-right w-3/6 text-[#4D4D4D] text-sm font-light">{{ $t('newsletter.phone_number') }}</th>
                    <th class="p-3 text-right w-2/6 text-[#4D4D4D] text-sm font-light">{{ $t('newsletter.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(phone, index) in filteredPhones" :key="index" class="border-b hover:bg-gray-50">
                    <td class="p-3 text-base text-[#0D0D0D]">{{ index + 1 }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">{{ phone }}</td>
                    <td class="p-3 text-base text-[#0D0D0D]">
                      <button 
                        @click="deletePhone(phone)"
                        class="text-[#0D0D0D] hover:text-red-600"
                      >
                        <i class="icon-smsir-trash-bin-trash text-2xl"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="mt-4 flex justify-between items-center">
              <div class="flex items-center space-x-2 rtl:space-x-reverse">
                <select v-model="perPage" class="input-main px-2 py-1 w-auto">
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <span class="text-sm text-[#4D4D4D]">{{ $t('common.itemsPerPage') }}</span>
              </div>
              
              <div class="flex space-x-2 rtl:space-x-reverse">
                <button 
                  @click="currentPage > 1 && (currentPage--)"
                  :disabled="currentPage === 1"
                  class="px-3 py-1 border rounded hover:bg-gray-100"
                  :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
                >
                  {{ $t('common.previous') }}
                </button>
                <span class="px-3 py-1 text-base text-[#0D0D0D]">
                  {{ currentPage }} / {{ totalPages }}
                </span>
                <button 
                  @click="currentPage < totalPages && (currentPage++)"
                  :disabled="currentPage >= totalPages"
                  class="px-3 py-1 border rounded hover:bg-gray-100"
                  :class="{ 'opacity-50 cursor-not-allowed': currentPage >= totalPages }"
                >
                  {{ $t('common.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConfirmation" class="modal">
      <div class="modal-content max-w-md">
        <div class="modal-header">
          <h2 class="text-[18px] text-[#0D0D0D]">{{ $t('newsletter.delete_confirm_title') }}</h2>
          <button @click="showDeleteConfirmation = false" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <p class="mb-4 text-[#4D4D4D]">{{ $t('newsletter.delete_confirm_message') }}</p>
        </div>
        
        <div class="modal-footer">
          <button @click="showDeleteConfirmation = false" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
            {{ $t('cancel') }}
          </button>
          <button @click="confirmDeletePhone" class="px-4 py-2 bg-red-500 text-white rounded-full flex items-center gap-2 text-base">
            <i class="icon-smsir-trash-bin-trash text-xl text-white"></i>
            {{ $t('remove') }}
          </button>
        </div>
      </div>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import LoadingSpinner from '../LoadingSpinner.vue';

export default {
  components: {
    LoadingSpinner
  },
  data() {
    return {
      phones: [],
      loading: true,
      searchQuery: '',
      currentPage: 1,
      perPage: 10,
      showDeleteConfirmation: false,
      phoneToDelete: null
    }
  },
  computed: {
    filteredPhones() {
      let filtered = this.phones;
      if (this.searchQuery) {
        filtered = this.phones.filter(phone => 
          phone.includes(this.searchQuery)
        );
      }
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return filtered.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.phones.length / this.perPage);
    }
  },
  methods: {
    async loadPhones() {
      this.loading = true;
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: 'sms_ir_get_newsletter_phones',
            nonce: window.sms_ir_login.nonce
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );

        if (response.data.success) {
          this.phones = response.data.data || [];
        } else {
          throw new Error(response.data.data);
        }
      } catch (error) {
        Swal.fire({
          text: this.$t('newsletter.load_error'),
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
      } finally {
        this.loading = false;
      }
    },
    deletePhone(phone) {
      this.phoneToDelete = phone;
      this.showDeleteConfirmation = true;
    },
    async confirmDeletePhone() {
      if (!this.phoneToDelete) return;

      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: 'sms_ir_delete_newsletter_phone',
            phone: this.phoneToDelete,
            nonce: window.sms_ir_login.nonce
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );

        if (response.data.success) {
          this.phones = this.phones.filter(p => p !== this.phoneToDelete);
          Swal.fire({
            text: this.$t('newsletter.delete_success'),
            icon: 'success',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: 'smsir-swal'
            }
          });
        } else {
          throw new Error(response.data.data);
        }
      } catch (error) {
        Swal.fire({
          text: this.$t('newsletter.delete_error'),
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
      } finally {
        this.showDeleteConfirmation = false;
        this.phoneToDelete = null;
      }
    },
    exportToExcel() {
      try {
        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(
          this.phones.map((phone, index) => ({
            'Row': index + 1,
            'Phone Number': phone
          }))
        );

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Subscribers');

        // Generate Excel file
        XLSX.writeFile(wb, 'newsletter_subscribers.xlsx');

        Swal.fire({
          text: this.$t('newsletter.export_success'),
          icon: 'success',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
      } catch (error) {
        Swal.fire({
          text: this.$t('newsletter.export_error'),
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'smsir-swal'
          }
        });
      }
    }
  },
  mounted() {
    this.loadPhones();
  }
}
</script>

<style scoped>
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #0D0D0D;
}

select {
  background-position: left 0.5rem center;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 30px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0D0D0D;
}

.modal-body {
  overflow-y: auto;
  flex: 1;
  padding: 0 16px;
}

.modal-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 16px;
}
</style>
